import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCleanupHistory } from '../../../../hooks/useCleanupHistory';
import FilterRow from './FilterRow';
import CleanupHistoryTable from './CleanupHistoryTable';
import CleanupHistoryInlineNotice from './CleanupHistoryInlineNotice';
import EmptyNoHistory from './EmptyNoHistory';
import EmptyNoFilterMatch from './EmptyNoFilterMatch';
import { DEFAULT_DATE, DEFAULT_JOB, DEFAULT_APPROVED, GRID_TEMPLATE } from './constants';

export function CleanupHistoryHeader() {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-3.5 md:px-8">
      <button
        type="button"
        onClick={() => navigate('/accounting/chart-of-accounts')}
        className="bg-transparent border-none cursor-pointer text-teal-dark text-[12.5px] font-semibold p-0 mb-2 inline-flex items-center gap-0.5"
      >
        &lsaquo; Back to Chart of accounts
      </button>
      <div className="text-[12.5px] text-ink3 mb-1 font-medium">
        <span className="cursor-pointer" onClick={() => navigate('/accounting')}>Accounting</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span className="cursor-pointer" onClick={() => navigate('/accounting/chart-of-accounts')}>Chart of accounts</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span>Cleanup history</span>
      </div>
      <h1 className="text-xl md:text-3xl font-extrabold m-0 -tracking-[0.3px] text-ink">Cleanup history</h1>
    </div>
  );
}

export default function CleanupHistoryPage() {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState(DEFAULT_DATE);
  const [jobFilter, setJobFilter] = useState(DEFAULT_JOB);
  const [approvedByFilter, setApprovedByFilter] = useState(DEFAULT_APPROVED);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, hasAnyHistory, undo } = useCleanupHistory({ dateFilter, jobFilter, approvedByFilter });

  const resetFilters = () => {
    setDateFilter(DEFAULT_DATE);
    setJobFilter(DEFAULT_JOB);
    setApprovedByFilter(DEFAULT_APPROVED);
  };

  const handleUndo = useCallback(
    (id: string) => {
      const outcome = undo(id);
      setNotice(outcome.message);
    },
    [undo]
  );

  if (!hasAnyHistory) {
    return (
      <div style={{ maxWidth: '100%', margin: 0, padding: '0 32px 90px' }}>
        <EmptyNoHistory onStartCleanup={() => navigate('/accounting/chart-of-accounts')} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100%', margin: 0, padding: '0 32px 90px' }}>
      <FilterRow
        dateFilter={dateFilter}
        jobFilter={jobFilter}
        approvedByFilter={approvedByFilter}
        onDateChange={setDateFilter}
        onJobChange={setJobFilter}
        onApprovedByChange={setApprovedByFilter}
      />
      <div className="bg-tableHeader border-y border-line py-2.5 px-3.5">
        <div className="grid gap-4 text-[10.5px] font-bold text-ink2 uppercase tracking-wider" style={{ gridTemplateColumns: GRID_TEMPLATE }}>
          <div>Date changed</div>
          <div>Job</div>
          <div>Approved by</div>
          <div>Description</div>
          <div className="text-right">Action</div>
        </div>
      </div>
      {data.length === 0 ? <EmptyNoFilterMatch onReset={resetFilters} /> : <CleanupHistoryTable rows={data} onUndo={handleUndo} />}
      {notice && <CleanupHistoryInlineNotice message={notice} onClose={() => setNotice(null)} />}
    </div>
  );
}